import React, { useState, useCallback, useEffect } from 'react';
import FullCalendar from '@fullcalendar/react';
import bootstrap5Plugin from '@fullcalendar/bootstrap5';
import resourceTimeGridDay from '@fullcalendar/resource-timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import {
  Container,
  Label,
  Button,
  Col,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Alert,
} from 'reactstrap';
import moment from 'moment';
import { FaCalendar, FaWalking } from 'react-icons/fa';

import NewBookingModal from './NewBookingModal';
import EditBookingModal from './EditBookingModal';
import ServiceList from './ServiceList';
import api from '../services/api';
import withWebSocket from '../contexts/withWebSocket';
import { useReferenceData, useDailyEvents, useDailyHolds } from '../hooks/useScheduleData';

const Calendar = ({
  selectedDate,
  hours,
  onToday,
  onSubmit,
  calendarRef,
  bookingDate,
  refreshToken,
  needsRefresh = 0,
}) => {
  const { resources: simulators, discountCodes } = useReferenceData();
  const { events, reload } = useDailyEvents(selectedDate, { activeOnly: true, refreshToken });
  const [selectedResourceType, setSelectedResourceType] = useState('');
  const { holds, reload: reloadHolds } = useDailyHolds(selectedDate, {
    activeOnly: true,
    resourceType: selectedResourceType,
    refreshToken,
  });

  useEffect(() => {
    if (needsRefresh > 0) {
      reload();
      reloadHolds();
    }
  }, [needsRefresh, reload, reloadHolds]);

  const [selectedStartTime, setSelectedStartTime] = useState('');
  const [selectedService, setSelectedService] = useState({ id: 0, title: '', duration: 60 });
  const [event, setEvent] = useState({});
  const [eventSource, setEventSource] = useState('Internal');
  const [newEvent, setNewEvent] = useState(false);
  const [editEvent, setEditEvent] = useState(false);
  const [resourceTypes, setResourceTypes] = useState([]);
  const [randomEventCount, setRandomEventCount] = useState(20);
  const [isRandomEventsConfirmOpen, setIsRandomEventsConfirmOpen] = useState(false);
  const [isSampleDataConfirmOpen, setIsSampleDataConfirmOpen] = useState(false);
  const [organization, setOrganization] = useState(null);

  // Fetch resource types from API
  useEffect(() => {
    const fetchResourceTypes = async () => {
      try {
        const res = await api.get('/resource-types/');
        if (res.data && res.data.length > 0) {
          setResourceTypes(res.data);
          setSelectedResourceType(res.data[0].id);
        }
      } catch (error) {
        console.error('Failed to fetch resource types:', error);
      }
    };
    fetchResourceTypes();
  }, []);

  // Fetch organization details
  useEffect(() => {
    const fetchOrganization = async () => {
      try {
        const res = await api.get('/get-org-details/');
        setOrganization(res.data);
      } catch (error) {
        console.error('Failed to fetch organization:', error);
      }
    };
    fetchOrganization();
  }, []);

  // Filter resources based on selected type
  const filteredResources = React.useMemo(() => {
    let filtered;
    if (!selectedResourceType) {
      filtered = simulators;
    } else {
      // selectedResourceType is already the ID, so compare directly
      filtered = simulators.filter(r => r.resource_type === Number(selectedResourceType));
    }
    // Sort by sort field
    return [...filtered].sort((a, b) => (a.sort || 0) - (b.sort || 0));
  }, [simulators, selectedResourceType]);

  // Get slot duration from selected resource type
  const slotDuration = React.useMemo(() => {
    if (!selectedResourceType || !resourceTypes.length) {
      return hours?.slot_duration || '00:15:00';
    }
    const selectedType = resourceTypes.find(rt => rt.id === Number(selectedResourceType));
    return selectedType?.timeslots || hours?.slot_duration || '00:15:00';
  }, [selectedResourceType, resourceTypes, hours]);


  const getEvent = useCallback(async (id) => {
    try {
      const res = await api.get(`/events/${id}`);
      setEvent(res.data);
      setSelectedStartTime(res.data.start);
      return res.data;
    } catch (error) {
      console.error('Failed to fetch event:', error);
      return null;
    }
  }, []);

  const handleBookingChange = useCallback(async (e) => {
    if (e.event.extendedProps.state === 'Hold') {
      e.revert();
      return;
    }

    const updatedEvent = await getEvent(e.event.id);
    if (updatedEvent) {
      setEvent({
        ...updatedEvent,
        start: e.event.startStr,
        end: e.event.endStr,
        revertFunc: e.revert,
      });
      setSelectedStartTime(e.event.startStr);
      setEditEvent(true);
    } else {
      e.revert();
    }
  }, [getEvent]);

  const handleBookingClick = useCallback((eventId) => {
    getEvent(eventId).then((eventData) => {
      if (eventData && eventData.state !== 'Hold') {
        setEditEvent(true);
      }
    });
  }, [getEvent]);

  const handleCalendarClick = useCallback((e) => {
    setSelectedStartTime(e.dateStr);
    setEventSource('');
    setNewEvent(true);
  }, []);

  const handleNewBooking = useCallback(() => {
    setEventSource('');
    setSelectedStartTime('');
    setNewEvent(true);
  }, []);

  const handleWalkInBooking = useCallback(() => {
    const now = moment();
    const minutes = Math.ceil(now.minute() / 15) * 15;
    const walkInTime = moment().minute(minutes).second(0).millisecond(0);

    setEventSource('Walk-in');
    setSelectedStartTime(walkInTime.format('YYYY-MM-DDTHH:mm:ssZ'));
    setSelectedService({ id: 999, title: 'Custom', duration: 60 });
    setNewEvent(true);
  }, []);

  const handleClose = useCallback(() => {
    if (event && event.revertFunc) {
      event.revertFunc();
    }

    setNewEvent(false);
    setEditEvent(false);
    setSelectedStartTime('');
    setSelectedService({ id: 0, title: '', duration: 60 });
    setEvent({});
  }, [event]);

  const handleSubmit = useCallback((date) => {
    handleClose();
    if (onSubmit) {
      onSubmit(date || selectedDate);
    }
  }, [handleClose, onSubmit, selectedDate]);

  // Daily Notes State and Handlers
  const [isDailyNotesOpen, setIsDailyNotesOpen] = useState(false);
  const [dailyNotesId, setDailyNotesId] = useState(null);
  const [dailyNotes, setDailyNotes] = useState('');

  const fetchDailyNotes = useCallback(async () => {
    try {
      const res = await api.get(`/daily-notes/?date=${moment(selectedDate).format('YYYY-MM-DD')}`);
      const note = res.data?.[0];
      setDailyNotesId(note?.id || null);
      setDailyNotes(note?.notes || '');
    } catch (error) {
      console.error('Failed to fetch daily notes:', error);
      setDailyNotesId(null);
      setDailyNotes('');
    }
  }, [selectedDate]);

  useEffect(() => {
    fetchDailyNotes();
  }, [fetchDailyNotes]);

  const handleOpenDailyNotes = useCallback(async () => {
    await fetchDailyNotes();
    setIsDailyNotesOpen(true);
  }, [fetchDailyNotes]);

  const handleCloseDailyNotes = useCallback(() => {
    setIsDailyNotesOpen(false);
    fetchDailyNotes();
  }, [fetchDailyNotes]);

  const handleClearDailyNotes = useCallback(() => {
    setDailyNotes('');
  }, []);

  const handleSaveDailyNotes = useCallback(() => {
    if (dailyNotesId) {
      // Update existing notes
      api.put(`/daily-notes/${dailyNotesId}/`, {
        date: moment(selectedDate).format('YYYY-MM-DD'),
        notes: dailyNotes,
      })
        .then(() => {
          setIsDailyNotesOpen(false);
          fetchDailyNotes();
        })
        .catch((error) => {
          console.error('Failed to update daily notes:', error);
        });
      return;
    }

    // Create new notes
    api.post('/daily-notes/', {
      date: moment(selectedDate).format('YYYY-MM-DD'),
      notes: dailyNotes,
    })
      .then(() => {
        setIsDailyNotesOpen(false);
        fetchDailyNotes();
      })
      .catch((error) => {
        console.error('Failed to save daily notes:', error);
      });
  }, [dailyNotesId, dailyNotes, selectedDate, fetchDailyNotes]);

  const handleAddRandomEvents = useCallback(() => {
    setIsRandomEventsConfirmOpen(true);
  }, []);

  const handleConfirmRandomEvents = useCallback(async () => {
    setIsRandomEventsConfirmOpen(false);
    try {
      const selectedType = resourceTypes.find(rt => rt.id === Number(selectedResourceType));
      const resourceTypeName = selectedType ? selectedType.name : 'aboutGolf Simulator';
      
      await api.post('/add-random-events/', {
        date: moment(selectedDate).format('YYYY-MM-DD'),
        count: randomEventCount,
        resource_type: resourceTypeName,
      });
      
      // Reload events after adding
      reload();
    } catch (error) {
      console.error('Failed to add random events:', error);
    }
  }, [selectedDate, randomEventCount, selectedResourceType, resourceTypes, reload]);

  const handleCancelRandomEvents = useCallback(() => {
    setIsRandomEventsConfirmOpen(false);
  }, []);

  const handleAddSampleData = useCallback(() => {
    setIsSampleDataConfirmOpen(true);
  }, []);

  const handleConfirmSampleData = useCallback(async () => {
    setIsSampleDataConfirmOpen(false);
    try {
      await api.post('/insert-sample-events/', {
        eventdate: moment(selectedDate).format('YYYY-MM-DD'),
      });
      
      // Reload events after adding
      reload();
    } catch (error) {
      console.error('Failed to add sample data:', error);
    }
  }, [selectedDate, reload]);

  const handleCancelSampleData = useCallback(() => {
    setIsSampleDataConfirmOpen(false);
  }, []);

  const filteredHolds = React.useMemo(() => {
    if (!selectedResourceType) {
      return holds;
    }

    const selectedTypeId = Number(selectedResourceType);
    const resourceTypeByResourceId = new Map(
      simulators.map((resource) => [Number(resource.id), Number(resource.resource_type)])
    );

    return holds.filter((hold) => {
      const directType = hold.resource_type || hold.service_resource_type || hold?.service?.resource_type || hold?.serviceDetails?.resource_type;
      if (directType !== undefined && directType !== null && directType !== '') {
        return Number(directType) === selectedTypeId;
      }

      const rawResourceId = hold.resource_id || hold.resource?.id || hold.resource;
      if (rawResourceId !== undefined && rawResourceId !== null && rawResourceId !== '') {
        return resourceTypeByResourceId.get(Number(rawResourceId)) === selectedTypeId;
      }

      return false;
    });
  }, [holds, selectedResourceType, simulators]);

  if (!hours) {
    return <Label>Closed</Label>;
  }

  const hasDailyNotes = (dailyNotes || '').trim().length > 0;

  const formatHoldPillLabel = (hold) => {
    const holdName = hold.customer_name || hold.customer || hold.title || hold.name || `Hold #${hold.id}`;
    const start = hold.start || hold.start_time;
    const end = hold.end || hold.end_time;
    const holdTime = start
      ? `${moment(start).format('h:mm A')}${end ? ` - ${moment(end).format('h:mm A')}` : ''}`
      : '';
    const resourceName = hold.resource_name || hold.resource || hold.resource_title || '';

    return [holdName, holdTime, resourceName].filter(Boolean).join(' | ');
  };

  const handleCancelHold = async (hold) => {
    const eventGuid = hold?.eventGuid || hold?.event_guid || hold?.guid;

    if (!eventGuid) {
      alert('Unable to cancel hold: missing event guid.');
      return;
    }

    try {
      await api.get(`/cancel-hold/?event=${eventGuid}`);
      reloadHolds();
    } catch (error) {
      console.error('Failed to cancel hold:', error);
      alert('Error canceling hold. Please try again.');
    }
  };

  return (
    <Container className="flex-grow-1 mt-3">
      <Row className="mb-3 align-items-center">

        {/* Left side: Date + Notes */}
        <Col>
          <Row className="align-items-center g-2 flex-nowrap">
            <Col xs="auto" className="fw-bold">
              {moment(selectedDate).format('dddd MMMM Do')}
            </Col>
            {!moment(selectedDate).isSame(moment(), 'day') && (
              <Col xs="auto">
                <Button
                  color="link"
                  className="text-secondary p-0 m-0"
                  onClick={onToday}
                >
                  <small>Go To Today</small>
                </Button>
              </Col>
            )}
          </Row>

          <Row className="mt-1">
            <Col xs="auto">
              <Button
                color={hasDailyNotes ? "warning" : "dark"}
                outline={!hasDailyNotes}
                className="p-0 px-2 py-0 rounded-pill"
                size="sm"
                style={{ fontSize: "0.75rem" }}
                onClick={handleOpenDailyNotes}
              >
                {hasDailyNotes ? 'See Notes' : 'Add Notes'}
              </Button>
            </Col>
          </Row>
        </Col>

        {moment(selectedDate).isSame(moment(), 'day') && (
          <Col>
            <Button block color="primary" onClick={handleWalkInBooking}>
              <FaWalking className="me-2" /> WALK IN
            </Button>
          </Col>
        )}

        {/* BOOK NOW */}
        <Col xs="3" sm="3" lg="4">
          <Button block color="success" onClick={handleNewBooking}>
            <FaCalendar className="me-2" /> BOOK NOW
          </Button>
        </Col>

      </Row>


      <FullCalendar
        key={moment(selectedDate).format('YYYY-MM-DD')}
        ref={calendarRef}
        plugins={[resourceTimeGridDay, bootstrap5Plugin, interactionPlugin]}
        initialView="resourceTimeGridDay"
        resources={filteredResources}
        resourceOrder='sort,title'
        events={events}
        editable
        selectable
        eventResourceEditable
        eventOverlap={false}
        allDaySlot={false}
        initialDate={selectedDate}
        headerToolbar=""
        slotMinTime={hours.start_time}
        slotMaxTime={hours.end_time}
        slotDuration={slotDuration}
        schedulerLicenseKey="CC-Attribution-NonCommercial-NoDerivatives"
        height="auto"
        themeSystem="bootstrap5"
        nowIndicator={false}
        eventClick={(e) => handleBookingClick(e.event.id)}
        eventDrop={handleBookingChange}
        eventChange={handleBookingChange}
        dateClick={handleCalendarClick}
        eventAllow={(dropInfo, draggedEvent) => draggedEvent.extendedProps.state !== 'Hold'}
      />

      {filteredHolds.length > 0 && (
        <Row className="mt-2">
          <Col className="d-flex flex-wrap align-items-center gap-2">
            <span className="text-muted small">Active Holds:</span>
            {filteredHolds.map((hold, index) => (
              <div
                key={hold.id || `hold-${index}`}
                className="badge rounded-pill text-bg-warning d-inline-flex align-items-center gap-2 px-3 py-2"
                style={{ fontSize: '0.875rem' }}
              >
                <span>{formatHoldPillLabel(hold)}</span>
                <button
                  type="button"
                  className="btn-close"
                  style={{ fontSize: '0.65rem' }}
                  onClick={() => handleCancelHold(hold)}
                  title="Cancel hold"
                  aria-label="Cancel hold"
                />
              </div>
            ))}
          </Col>
        </Row>
      )}

      <Row className="mt-2 align-items-center">
        {organization?.demo_mode && (
          <Col xs="auto">
            <Row className="g-2 align-items-center">
              <Col xs="auto">
                <Input
                  type="number"
                  id="randomEventCount"
                  value={randomEventCount}
                  onChange={(e) => setRandomEventCount(parseInt(e.target.value) || 0)}
                  bsSize="sm"
                  style={{ width: '60px' }}
                  min="1"
                  max="99"
                />
              </Col>
              <Col xs="auto">
                <Button
                  color="light"
                  size="sm"
                  onClick={handleAddRandomEvents}
                >
                  Add Random Events
                </Button>
              </Col>
            </Row>
          </Col>
        )}
        {organization?.demo_mode && (
          <Col xs="auto">
            <Button color="light" size="sm" onClick={handleAddSampleData}>
              Add Sample Data
            </Button>
          </Col>
        )}
        <Col xs="auto" className="ms-auto">
          <Input
            type="select"
            id="resourceTypeFilter"
            value={selectedResourceType}
            onChange={(e) => setSelectedResourceType(e.target.value)}
            bsSize="sm"
          >
            {resourceTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </Input>
        </Col>
      </Row>

      <ServiceList />

      <NewBookingModal
        key={`new-${selectedStartTime}-${selectedResourceType}-${newEvent}`}
        open={newEvent}
        onClose={handleClose}
        bookingDate={bookingDate || selectedDate}
        bookingTime={selectedStartTime}
        service={selectedService}
        resources={filteredResources}
        discountCodes={discountCodes}
        onToday={onToday}
        onSubmit={handleSubmit}
        source={eventSource}
        resourceType={selectedResourceType}
      />

      <EditBookingModal
        key={event.id}
        open={editEvent}
        event={event}
        bookingDate={bookingDate || selectedDate}
        bookingTime={selectedStartTime}
        service={selectedService}
        resources={simulators}
        discountCodes={discountCodes}
        onClose={handleClose}
        onToday={onToday}
        onSubmit={handleSubmit}
        resourceType={selectedResourceType}
      />

      <DailyNotesModal
        open={isDailyNotesOpen}
        onClose={handleCloseDailyNotes}
        onSave={handleSaveDailyNotes}
        onClear={handleClearDailyNotes}
        onChange={(e) => setDailyNotes(e.target.value)}
        date={selectedDate}
        notes={dailyNotes}
      />

      <Modal isOpen={isRandomEventsConfirmOpen} toggle={handleCancelRandomEvents}>
        <ModalHeader toggle={handleCancelRandomEvents}>Confirm Add Random Events</ModalHeader>
        <ModalBody>
          <p>Are you sure you want to add {randomEventCount} random events?</p>
          <Alert color="warning">
            This action will delete all existing events for this date and resource type before adding new random events.
          </Alert>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={handleCancelRandomEvents}>
            Cancel
          </Button>
          <Button color="danger" onClick={handleConfirmRandomEvents}>
            Yes, Add Random Events
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={isSampleDataConfirmOpen} toggle={handleCancelSampleData}>
        <ModalHeader toggle={handleCancelSampleData}>Confirm Add Sample Data</ModalHeader>
        <ModalBody>
          <p>Are you sure you want to add sample events?</p>
          <Alert color="warning">
            This action will delete all existing events for this date before adding new sample events.
          </Alert>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={handleCancelSampleData}>
            Cancel
          </Button>
          <Button color="danger" onClick={handleConfirmSampleData}>
            Yes, Add Sample Data
          </Button>
        </ModalFooter>
      </Modal>
    </Container>
  );
};

const DailyNotesModal = ({
  open,
  onClose,
  onSave,
  onClear,
  onChange,
  date,
  notes,
}) => (
  <Modal isOpen={open} toggle={onClose} size="md">
    <ModalHeader toggle={onClose}>{moment(date).format('dddd MMMM Do')} Notes</ModalHeader>
    <ModalBody>
      <Label for="daily-notes-textarea" className="fw-semibold">
        Daily Notes
      </Label>
      <Input
        type="textarea"
        id="daily-notes-textarea"
        value={notes}
        onChange={onChange}
        placeholder="Enter notes for the day..."
        rows={12}
      />
    </ModalBody>
    <ModalFooter className="w-100">
      <div className="d-flex justify-content-between w-100">
        <Button color="outline-secondary" onClick={onClear}>
          Clear Notes
        </Button>
        <div className="d-flex gap-2">
          <Button color="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button color="primary" onClick={onSave}>
            Save
          </Button>
        </div>
      </div>
    </ModalFooter>
  </Modal>
);

export default withWebSocket(Calendar);
